import React from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { IconHeartFilled } from '@tabler/icons-react';

import { HubPropertyPhoto } from './HubPropertyPhoto';
import type { HubSectionId } from './hubSectionOrdering';
import { useHubSectionTracking } from './useHubSectionTracking';

/**
 * Glassy "saved" indicator overlaid on the photo. Decorative: every card in
 * this carousel is a favorite, so it is not a toggle.
 */
const SavedIndicator: React.FC = () => (
  <Box
    aria-hidden
    className="absolute top-2 right-2 h-[26px] w-[26px] items-center justify-center rounded-full border border-[rgba(255,255,255,0.38)] bg-[rgba(255,255,255,0.5)] backdrop-blur-[7px]"
  >
    {/* Coral pink matching the Opendoor mobile app's saved/favorite heart; the
        web design system has no pink token, so this one-off hex stands in. */}
    <IconHeartFilled size={16} className="text-[#ff8082]" />
  </Box>
);

export interface SavedHomeCardProps {
  section: HubSectionId;
  href: string;
  photoUrl: string | null;
  addressLine: string;
  priceLabel: string | null;
  detailsLine: string | null;
}

/**
 * Compact saved-home card for the Hub carousel: rounded photo with a saved
 * indicator, then street, price, and the bed/bath/sqft spec line below.
 * Intentionally lighter than the marketplace `PropertyCard` (no favorite
 * toggle or monthly-payment estimate) to match the Hub design.
 */
export const SavedHomeCard: React.FC<SavedHomeCardProps> = ({
  section,
  href,
  photoUrl,
  addressLine,
  priceLabel,
  detailsLine,
}) => {
  const trackSectionInteraction = useHubSectionTracking(section);

  return (
    <Col
      href={href}
      aria-label={addressLine}
      analyticsName="hub-saved-home-card"
      onClick={trackSectionInteraction}
      className="w-[min(74vw,260px)] no-underline"
    >
      <Box className="relative h-[145px] w-full overflow-hidden rounded-xl">
        <HubPropertyPhoto photoUrl={photoUrl} />
        <SavedIndicator />
      </Box>
      <Col className="gap-1 pt-3 pb-4">
        <Text as="span" className="min-w-0 truncate text-label-md text-od-content-primary">
          {addressLine}
        </Text>
        {priceLabel && (
          <Text as="span" className="text-body-sm text-od-content-secondary">
            {priceLabel}
          </Text>
        )}
        {detailsLine && (
          <Text as="span" className="min-w-0 truncate text-body-sm text-od-content-secondary">
            {detailsLine}
          </Text>
        )}
      </Col>
    </Col>
  );
};
